import { useEffect, useState } from "react";
import { MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, SectionHeading } from "../components/Reveal";
import { AbstractVisual } from "../components/AbstractVisual";
import { IndiaMap } from "../components/IndiaMap";
import { api } from "../lib/api";

const FILTERS = ["All", "Operating", "Upcoming", "Pop-Up"];

export default function Locations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.list("locations").then(setItems).catch(() => {});
  }, []);

  const shown = filter === "All" ? items : items.filter((l) => l.status === filter);

  const onSelect = (slug) => {
    setSelected(slug);
    setFilter("All");
    setTimeout(() => {
      document.getElementById(`location-card-${slug}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  return (
    <main className="pt-40 pb-28">
      <Seo title="Locations" path="/locations"
        description="Vrova immersive venues across India — Hyderabad flagship, Mumbai, Bengaluru, Delhi NCR, Chennai, Pune. XR experiences Hyderabad." />
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <SectionHeading overline="Locations"
          title={<>Where Worlds <span className="italic text-zinc-400">Open Their Doors.</span></>} />
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 mt-16 items-center">
          <Reveal className="lg:col-span-5">
            <IndiaMap locations={items} selected={selected} onSelect={onSelect} />
          </Reveal>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="flex flex-wrap gap-2.5 mb-10" data-testid="location-filters">
                {FILTERS.map((f) => (
                  <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`px-6 py-2.5 rounded-full text-xs font-mono uppercase tracking-[0.15em] transition-all duration-300 ${filter === f ? "bg-white text-black" : "border border-white/15 text-zinc-400 hover:text-white hover:border-white/35"}`}>
                    {f}
                  </button>
                ))}
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="locations-grid">
              {shown.map((l, i) => (
                <Reveal key={l.id} delay={(i % 2) * 0.07}>
                  <div id={`location-card-${l.slug}`} data-testid={`location-card-${l.slug}`}
                    onClick={() => setSelected(l.slug)}
                    className={`group relative overflow-hidden rounded-2xl border bg-white/[0.02] cursor-pointer hover:-translate-y-1.5 transition-all duration-500 poster-sheen ${selected === l.slug ? "border-white/45 shadow-[0_0_50px_rgba(255,255,255,0.07)]" : "border-white/10 hover:border-white/25"}`}>
                    <div className="relative h-28"><AbstractVisual hue={l.hue ?? 222} seed={i * 7 + 2} /></div>
                    <div className="p-7">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-serif text-xl tracking-tight flex items-center gap-2.5"><MapPin size={16} className="text-zinc-500" />{l.city}</h3>
                        <span className={`text-[10px] font-mono uppercase tracking-[0.15em] px-3 py-1.5 rounded-full ${l.status === "Operating" ? "bg-white text-black" : "border border-white/15 text-zinc-400"}`}>
                          {l.status}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-300 mb-2">{l.venue}</p>
                      <p className="text-sm text-zinc-500 font-light leading-relaxed mb-4">{l.detail}</p>
                      <p className="text-[11px] font-mono text-zinc-600">{l.experiences}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
